import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useDisclosure } from '@/lib/hooks';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useAuthStore } from "../store/authStore";
import { useOfficeSpaceStore } from "../store/officeSpaceStore";
import UserMenu from "../components/common/UserMenu";
import PageMenu from "../components/common/PageMenu";
import LanguageSwitcher from "../components/common/LanguageSwitcher";
import { useColorScheme } from "../components/common/ColorSchemeToggle";
import Logo from "../components/common/Logo";
import OfficeSpaceSwitcher from "../components/common/OfficeSpaceSwitcher";
import { userMenuItems } from "../constants/menuItems";
import { ModuleGlyph, MY_DOCS_ICON_COLOR, isSelfColored, tileGlyphSrc } from "../constants/moduleColors";
import {
  IconLogout, IconUser,
  IconHome, IconFileText, IconPhoto,
  IconPlus, IconX, IconBell, IconChevronRight, IconChevronDown,
  IconSparkles, IconPrinter, IconTemplate, IconBuilding, IconMenu2,
  IconCurrencyTaka, IconBrandWhatsapp, IconPlayerPlay, IconCoins, IconPencil, IconCamera,
  IconShoppingCart, IconSettings, IconSearch,
  IconLayoutDashboard, IconFolders, IconBriefcase, IconTools, IconStack2,
  IconTrophy, IconForms, IconBuildingBank, IconClipboardList,
  IconFileInvoice, IconUsers, IconReceipt2, IconArrowsExchange, IconLanguage, IconUsersGroup,
  IconDownload, IconMoon, IconSun, IconKeyboard,
} from '@tabler/icons-react';
import Footer from "../components/common/Footer";
import { useIncomeExpenseStore } from "../store/incomeExpenseStore";
import UserShortcutHelpModal from "../components/common/UserShortcutHelpModal";
import ProfileCompletionBanner from "../components/common/ProfileCompletionBanner";
import ContestBanner from "../components/common/ContestBanner";
import MaintenanceBanner from "../components/common/MaintenanceBanner";
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import aiTemplateCategoryService from '../services/aiTemplateCategoryService';
import printTemplateCategoryService from '../services/printTemplateCategoryService';
import { useBuyCreditPath } from '../store/paymentConfigStore';
import megamenuData from '../../megamenu.json';
import { MOBILE_APP_URL } from '../config/appLinks';

// Opens the global command palette (GlobalSearch listens for this event).
const openGlobalSearch = () => window.dispatchEvent(new Event('global-search:open'));

// Real SohozTools count (mirrors PageMenu) — used for the sidebar badge.
const TOOL_COUNT = megamenuData.categories.reduce((n, c) => n + c.tools.length, 0);

const NO_FOOTER_ROUTES    = ['/onboard', '/print-media/editor/'];
const NO_NAV_ROUTES       = ['/onboard', '/print-media/editor/'];
const NO_BOTTOM_NAV_ROUTES = ['/generate-image', '/generate-image-manual', '/onboard', '/print-media/editor/'];

// A signed-in account whose role the web app refuses (see config/loginRoles). The auth store already refuses such a role at
// sign-in and when a saved session is restored, so this is only a safety net. It signs out instead of redirecting: sending
// the account to "/" would hit this same guard again and loop. Once signed out, the layout sends it to /login.
function SignOutNow() {
  const logout = useAuthStore((s) => s.logout);
  useEffect(() => { logout(); }, [logout]);
  return null;
}

// ─────────────────────────────────────────────────────────────
// Root layout
// ─────────────────────────────────────────────────────────────
function PrivateLayout({ allowedRoles = ["OWNER", "OPERATOR"] }) {
  const { isAuthenticated, user, getCurrentUser } = useAuthStore();
  const [drawerOpen, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure();
  const [createOpen, setCreateOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { toggleModal: toggleIncomeExpense } = useIncomeExpenseStore();
  const showIEBtn = user?.settings?.showIncomeExpenseBtn !== false;

  // Bangladesh-only features (Question Papers, Jobs, Useful/Dorkari Links) are hidden from
  // the top header for users whose country is set to a non-Bangladesh country.
  const isBdUser = !user?.country || user.country === 'BD';

  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    if (isAuthenticated) {
      getCurrentUser().catch(() => {}).finally(() => setAuthReady(true));
    } else {
      setAuthReady(true);
    }
  }, []);

  // Office space init
  const { officeSpaces, selectedOfficeSpace, fetchOfficeSpaces, selectOfficeSpace, isLoading: isOfficeSpaceLoading } = useOfficeSpaceStore();

  // When the user manages more than one business profile (shop), the চাকরি (Jobs)
  // menu is pushed out of the already-crowded main header into the sidebar drawer,
  // freeing room for the active-shop switcher. Single-shop users keep the normal
  // responsive header⇄sidebar placement. MobileDrawer mirrors this same check so
  // Jobs always lives in exactly one place.
  const hasMultipleShops = officeSpaces.length > 1;
  const headerMenuItems = userMenuItems.filter((it) => {
    // Question Papers is no longer shown in the main header — it was crowding the
    // nav (worse with the multi-shop switcher) and pushing it to overflow. It now
    // lives only in the sidebar drawer (MobileDrawer, always visible there).
    if (it.path === '/questions-create/create') return false;
    if (!isBdUser && ['/template-sets', '/jobs', '/dorkari-links'].includes(it.path)) return false;
    if (hasMultipleShops && it.path === '/jobs') return false;
    return true;
  });
  // Header tabs the nav had to give up for lack of room (PageMenu measures); the drawer shows them instead.
  const [fitHidden, setFitHidden] = useState(() => new Set());
  const hasInitiallyFetched = useRef(false);
  const wasLoading = useRef(false);

  useEffect(() => {
    if (user && (user.role === 'OWNER' || user.role === 'OPERATOR')) fetchOfficeSpaces();
  }, [user]);

  useEffect(() => {
    if (isOfficeSpaceLoading) { wasLoading.current = true; }
    else if (wasLoading.current) { hasInitiallyFetched.current = true; }
  }, [isOfficeSpaceLoading]);

  useEffect(() => {
    if (!hasInitiallyFetched.current || isOfficeSpaceLoading) return;
    if (officeSpaces.length === 0) { if (selectedOfficeSpace) selectOfficeSpace(null); return; }
    if (!selectedOfficeSpace) { selectOfficeSpace(officeSpaces[0]); return; }
    const isStillValid = officeSpaces.some(os => os.id === selectedOfficeSpace.id);
    if (!isStillValid) selectOfficeSpace(officeSpaces[0]);
  }, [officeSpaces, selectedOfficeSpace, isOfficeSpaceLoading]);

  const showFooter    = !NO_FOOTER_ROUTES.some(p => location.pathname.startsWith(p));
  const showNav       = !NO_NAV_ROUTES.some(p => location.pathname.startsWith(p));
  const showBottomNav = !NO_BOTTOM_NAV_ROUTES.some(p => location.pathname.startsWith(p));
  // All pages get a full-page #fbfaf8 background on large screens (≥1024px) while the
  // nav bar stays white, so the centered content surface reads against a soft gray band.

  if (!isAuthenticated || !user) {
    const redirect = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }
  if (!allowedRoles.includes(user.role)) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    return <SignOutNow />;
  }

  // A Google sign-up has no real phone yet (the backend stores the placeholder `g:<google id>`): until the number is saved on the
  // onboarding page, every other page sends them back there — typing a URL, reloading or signing in again changes nothing.
  const needsPhone = user.role !== 'admin' && String(user.phone || '').startsWith('g:');
  if (needsPhone && location.pathname !== '/onboard') {
    return <Navigate to="/onboard" replace />;
  }

  // Onboarding gate — prevent already-onboarded users from revisiting /onboard
  if (user.role !== 'admin' && user.isOnboarded && !needsPhone && location.pathname === '/onboard') {
    return <Navigate to="/" replace />;
  }

  // Full-screen routes bypass the container wrapper entirely
  if (location.pathname === '/onboard' || location.pathname.startsWith('/print-media/editor/')) {
    return (
      <div className="fixed inset-0 z-[80] bg-white dark:bg-zinc-950 overflow-hidden">
        <Outlet />
      </div>
    );
  }

  // Important quick links shown in the tablet-width header (between phone and desktop).
  // Icons mirror the desktop nav — the same hero-card module SVGs (public/module-icons).
  const tabletQuickLinks = [
    { path: '/template-sets',         labelKey: 'navigation.documentSets',        iconImg: '/module-icons/document-set.svg' },
    { path: '/generate-image',        labelKey: 'navigation.generateImageAI',     iconImg: '/module-icons/ai-tools.svg' },
    { path: '/generate-image-manual', labelKey: 'navigation.generateImageManual', iconImg: '/module-icons/manual-photo-edit.svg' },
    { path: '/ai-templates',          labelKey: 'navigation.aiTemplates',         iconImg: '/module-icons/ai-templates.svg' },
  ].filter((it) => isBdUser || it.path !== '/template-sets');

  return (
    <div className="min-h-screen flex flex-col">

      {showNav && (
        <header className="main-nav-wrapper w-full sticky nav:static top-0 z-[60] nav:z-auto bg-white dark:bg-zinc-950 nav:border-b nav:border-zinc-200 dark:border-zinc-700 px-4 sm:px-6 xl:px-8">
            {/* ── Service maintenance notice (toggle via SHOW flag in the component) ── */}
            {/* <MaintenanceBanner /> */}

            {/* ── Sliding contest announcement (live contest notice, or the "starts soon" teaser) ── */}
            <ContestBanner />

            {/* ── Desktop nav (≥860px) — single row ── */}
            <div className="hidden nav:block font-hind">
              <div className="relative">
                <div className="flex items-center gap-3 xl:gap-4 py-2.5">
                  {/* Brand logo */}
                  <Link to="/" className="shrink-0 flex items-center mr-1">
                    <Logo w={120} />
                  </Link>
                  {/* Nav menu fills the middle (its dropdowns must stay un-clipped).
                      "সব মেনু" renders as the trailing tab, replacing the old "আরও" menu. */}
                  <PageMenu
                    menuItems={headerMenuItems}
                    fitHidden={fitHidden}
                    onFitChange={setFitHidden}
                    trailing={
                      <button
                        type="button"
                        onClick={toggleDrawer}
                        aria-label={t('navigation.allMenu')}
                        className="flex items-center gap-1 min-[1100px]:gap-1.5 whitespace-nowrap text-[12.5px] min-[1100px]:text-[13.5px] font-bold px-1.5 min-[1100px]:px-2.5 py-2 rounded-lg text-[#444] dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors focus:outline-none"
                      >
                        <IconMenu2 size={16} className="shrink-0" />
                        {t('navigation.allMenu')}
                      </button>
                    }
                  />
                  {/* Right: search + profile (theme / language / shortcuts moved into the profile menu) */}
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Active-shop switcher — only when the user belongs to more than one */}
                    {officeSpaces.length > 1 && (
                      <div className="rounded-[9px] border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
                        <OfficeSpaceSwitcher />
                      </div>
                    )}
                    {/* Compact profile-completion badge (only shows when incomplete) */}
                    <ProfileCompletionBanner authReady={authReady} />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={openGlobalSearch}
                          aria-label={t('common.search')}
                          className="w-[38px] h-[38px] rounded-[9px] border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                          <IconSearch size={18} stroke={1.8} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">{t('common.search')}</TooltipContent>
                    </Tooltip>
                    <UserMenu />
                  </div>
                </div>
                {/* Full-width megamenu renders into this slot so it spans the whole
                    container, not just the nav's middle column. */}
                <div id="sohoz-megamenu-slot" className="absolute left-0 right-0 top-full z-[200] max-w-container mx-auto" />
              </div>
              {/* Profile-completion nudge now lives inside the profile dropdown (UserMenu). */}
            </div>

            {/* ── Mobile / tablet top bar (<1024px) ── */}
            <div className="nav:hidden flex items-center justify-between gap-3 py-2.5 font-hind">
              {/* Left-aligned logo */}
              <Link to="/" className="flex items-center shrink-0">
                <Logo w={110} />
              </Link>

              {/* Tablet quick links (hidden on small phones) */}
              <TabletQuickLinks items={tabletQuickLinks} />

              {/* Right: switcher (multi-shop only) + search + profile avatar */}
              <div className="flex items-center gap-1.5 shrink-0">
                {officeSpaces.length > 1 && (
                  <div className="rounded-lg border border-zinc-200 dark:border-zinc-700">
                    <OfficeSpaceSwitcher />
                  </div>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={openGlobalSearch}
                      aria-label={t('common.search')}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                    >
                      <IconSearch size={18} stroke={1.8} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{t('common.search')}</TooltipContent>
                </Tooltip>
                <button
                  type="button"
                  onClick={toggleDrawer}
                  aria-label={t('navigation.allMenu')}
                  className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center shrink-0 focus:outline-none border border-zinc-200 dark:border-zinc-700"
                >
                  {user?.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt={user?.name || 'User'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full bg-blue-600 text-white flex items-center justify-center text-[13px] font-semibold">
                      {(user?.name || 'U').trim().split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                    </span>
                  )}
                </button>
              </div>
            </div>
        </header>
      )}

      {/* The two template galleries drop the max-width cap — a masonry grid
          beside a filter sidebar needs the whole screen. The AI photo editor gets a wider cap for its left sidebar. */}
      <div className={`${['/ai-templates', '/print-media', '/studio-editor'].includes(location.pathname) ? 'max-w-none' : location.pathname === '/generate-image' ? 'max-w-[1508px]' : 'max-w-container'} mx-auto px-4 sm:px-6 w-full flex flex-col flex-1`}>
        {/* Main content — extra bottom padding on mobile for bottom nav */}
        <div className={`flex-1 ${showBottomNav ? 'pb-[76px] nav:pb-0' : ''}`}>
          <Outlet />
        </div>

        {showFooter && <div className="hidden nav:block"><Footer /></div>}
      </div>

      {/* ── Mobile Left Drawer ── */}
      {showNav && (
        <MobileDrawer
          fitHidden={fitHidden}
          open={drawerOpen}
          onClose={closeDrawer}
          authReady={authReady}
          showIEBtn={showIEBtn}
          onOpenIncomeExpense={() => { closeDrawer(); toggleIncomeExpense(); }}
        />
      )}

      {/* ── Mobile Bottom Nav ── */}
      {showNav && showBottomNav && (
        <MobileBottomNav onOpenCreate={() => setCreateOpen(true)} />
      )}

      {/* ── Create Bottom Sheet ── */}
      <MobileCreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Tablet quick links — plain tap-to-navigate buttons, except AI Templates
// which opens a tap-triggered dropdown of published categories (mirrors the
// desktop nav's hover dropdown, but touch-friendly).
// ─────────────────────────────────────────────────────────────
function TabletQuickLinks({ items }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const isBn = currentLanguage === 'bn';
  const [aiCategories, setAiCategories] = useState([]);
  const [printCategories, setPrintCategories] = useState([]);

  useEffect(() => {
    aiTemplateCategoryService
      .list()
      .then((r) => setAiCategories((r?.data || []).filter((c) => c.status === 'published')))
      .catch(() => {});
    printTemplateCategoryService
      .list()
      .then((r) => setPrintCategories((r?.data || []).filter((c) => c.status === 'published')))
      .catch(() => {});
  }, []);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const btnCls = (active) => cn(
    'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium whitespace-nowrap transition-colors',
    active
      ? 'bg-primary/10 text-zinc-900 dark:bg-primary/20 dark:text-zinc-100 [&_svg]:text-primary'
      : 'text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
  );

  // Mirrors the desktop ModuleIcon — module glyph tinted in its brand colour,
  // matching the colourful dashboard module cards.
  const QuickIcon = ({ src }) => <ModuleGlyph src={src} />;

  return (
    <div className="hidden md:flex items-center gap-1 flex-1 justify-center min-w-0">
      {items.map((item) => {
        const active = isActive(item.path);

        // AI templates and print templates each browse by their own category
        // table, so both tabs open the same category picker.
        const categories = item.path === '/ai-templates' ? aiCategories
          : item.path === '/print-media' ? printCategories
          : null;
        if (categories) {
          return (
            <DropdownMenu key={item.path}>
              <DropdownMenuTrigger asChild>
                <button className={btnCls(active)}>
                  <QuickIcon src={item.iconImg} />
                  <span>{t(item.labelKey)}</span>
                  <IconChevronDown size={13} className="shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="font-hind max-h-[70vh] overflow-y-auto">
                <DropdownMenuItem className="cursor-pointer text-sm" onClick={() => navigate(item.path)}>
                  {isBn ? 'সকল টেমপ্লেট' : 'All Templates'}
                </DropdownMenuItem>
                {categories.map((cat) => (
                  <DropdownMenuItem
                    key={cat.id}
                    className="cursor-pointer text-sm"
                    onClick={() => navigate(`${item.path}?category=${cat.slug}`)}
                  >
                    {isBn && cat.nameBn ? cat.nameBn : cat.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={cn(btnCls(active), item.path === '/generate-image-manual' && 'hidden min-[850px]:flex')}
          >
            <QuickIcon src={item.iconImg} />
            <span>{t(item.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Mobile bottom navigation bar
// ─────────────────────────────────────────────────────────────
function MobileBottomNav({ onOpenCreate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isBdUser = !user?.country || user.country === 'BD';

  const navItems = [
    { id: 'home',     path: '/',              label: 'হোম',      Icon: IconHome },
    { id: 'docs',     path: '/template-sets',  label: 'ডকুমেন্ট', Icon: IconFileText },
    { id: 'create',   path: null,             label: null,        Icon: null },
    { id: 'photo',    path: '/generate-image',label: 'ছবি',      Icon: IconPhoto },
    { id: 'profile',  path: '/profile',       label: 'প্রোফাইল', Icon: IconUser },
  ].filter((it) => isBdUser || it.path !== '/template-sets');

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="nav:hidden fixed bottom-0 left-0 right-0 z-[50] bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border-t border-zinc-200 dark:border-zinc-700 shadow-[0_-4px_24px_rgba(0,0,0,0.12)]" style={{ overflow: 'visible' }}>
      <div className="grid grid-cols-5 items-center px-2 pt-1 pb-2" style={{ overflow: 'visible' }}>
        {navItems.map((item) => {
          if (item.id === 'create') {
            return (
              <div key="create" className="flex justify-center relative z-[51]" style={{ overflow: 'visible' }}>
                <button
                  onClick={onOpenCreate}
                  aria-label="Create new"
                  className="w-12 h-12 -mt-4 relative z-[51] rounded-full flex items-center justify-center text-white shadow-[0_8px_22px_-8px_rgba(244,123,22,0.6)] border-[3px] border-white dark:border-zinc-900 bg-[linear-gradient(135deg,#FBA53A_0%,#F4791A_52%,#EB5E10_100%)]"
                >
                  <IconPlus size={22} stroke={2.5} />
                </button>
              </div>
            );
          }
          const active = isActive(item.path);
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-0.5 py-0.5 px-1 transition-colors ${
                active ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'
              }`}
            >
              <item.Icon size={21} stroke={active ? 2 : 1.75} />
              <span className="text-[10px] font-semibold leading-none mt-0.5">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────
// Mobile left drawer
// ─────────────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, authReady, showIEBtn, onOpenIncomeExpense, fitHidden }) {
  const { t, currentLanguage } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { officeSpaces } = useOfficeSpaceStore();
  const hasMultipleShops = officeSpaces.length > 1;
  const buyCreditPath = useBuyCreditPath();
  const [shortcutsOpen, setShortcutsOpen] = useState(false); // keyboard-shortcuts dialog, opened from a menu row

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  };
  const toBn = (s) => String(s).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);
  const handleLogout = () => { onClose(); logout(); navigate('/login'); };
  const handleProfileClick = () => { onClose(); navigate('/profile'); };

  // Credit items — mirror the desktop profile dropdown (UserMenu), which is
  // hidden on mobile. Operators spend the owner's credit, so it's hidden for them.
  const isOperator = user?.role === 'OPERATOR';
  const showCredit = !!user && user.role !== 'admin' && !isOperator && user.credit != null;
  const fmtCredit = (n) => currentLanguage === 'bn' ? toBn(n ?? 0) : (n ?? 0).toLocaleString();

  // Bangladesh-only features (Jobs, Question Papers, Useful/Dorkari Links) are hidden for
  // users whose country is set to anything other than Bangladesh. Unset country → shown.
  const isBdUser = !user?.country || user.country === 'BD';
  const BD_ONLY_PATHS = ['/template-sets', '/questions-create/create', '/jobs', '/dorkari-links'];

  // Header ⇄ sidebar dependency: every item that can appear in the main header
  // carries a `hideClass` that is the exact MIRROR of its header breakpoint
  // (userMenuItems navClass / the always-visible desktop nav at `nav:` 1024px).
  // Below the breakpoint the menu lives here in the sidebar; at/above it, it
  // moves to the header and disappears from the sidebar.
  // Group 1 — top, unlabeled: quick access to core account pages.
  const primaryItems = [
    { path: '/',          labelKey: 'navigation.dashboard',   Icon: IconLayoutDashboard },
    { path: '/documents', labelKey: 'navigation.myDocuments', Icon: IconFileText,
      iconClass: MY_DOCS_ICON_COLOR, hideClass: 'nav:hidden' },
    // Income/Expense moved to its own "Accounts & Invoice" section below.
    // Contest is hidden for operators (they compete under the owner's account).
    ...(user?.role !== 'OPERATOR'
      ? [{ path: '/contest', labelKey: 'navigation.contest', Icon: IconTrophy, iconClass: 'text-amber-500' }]
      : []),
  ];

  // Group 2 — "Features & Tools": the main feature surfaces.
  const toolItems = [
    { path: '/template-sets',         labelKey: 'navigation.documentSets',        Icon: IconFolders, iconImg: '/module-icons/document-set.svg' },
    { path: '/generate-image',        labelKey: 'navigation.generateImageAI',     Icon: IconSparkles, iconImg: '/module-icons/ai-tools.svg',
      hideClass: 'nav:hidden' },
    { path: '/generate-image-manual', labelKey: 'navigation.generateImageManual', Icon: IconPencil, iconImg: '/module-icons/manual-photo-edit.svg',
      hideClass: 'min-[1500px]:hidden' },
    { path: '/studio-editor',         labelKey: 'navigation.studioEditor',        Icon: IconCamera, iconImg: '/module-icons/studio-editor.svg',
      badge: { tone: 'orange', text: currentLanguage === 'bn' ? 'নতুন' : 'New' }, hideClass: 'min-[1290px]:hidden' },
    { path: '/bulk-photo-edit',       labelKey: 'navigation.bulkPhotoEdit',       Icon: IconStack2, iconImg: '/module-icons/bulk-photo-editing.svg',
      hideClass: 'min-[1390px]:hidden' },
    { path: '/ai-documents',          labelKey: 'navigation.aiDocuments',         Icon: IconSparkles, iconImg: '/module-icons/ai-documents.svg',
      badge: { tone: 'orange', text: currentLanguage === 'bn' ? 'নতুন' : 'New' }, hideClass: 'min-[1600px]:hidden' },
    { path: '/ai-templates',          labelKey: 'navigation.aiTemplates',         Icon: IconTemplate, iconImg: '/module-icons/ai-templates.svg',
      hideClass: 'nav:hidden' },
    // Question Papers is no longer in the main header, so it always lives here in
    // the sidebar (no header-mirror hideClass).
    { path: '/questions-create/create', labelKey: 'navigation.questionPapers',    Icon: IconClipboardList, iconImg: '/questions-create.svg' },
    { path: '/print-media',           labelKey: 'navigation.printMedia',          Icon: IconPrinter, iconImg: '/module-icons/print-media.svg',
      hideClass: 'min-[1700px]:hidden' },
    { path: '/tools',                 labelKey: 'navigation.sohozTools',          Icon: IconTools, iconImg: '/module-icons/easy-tools.svg',
      badge: { tone: 'blue', text: currentLanguage === 'bn' ? `${toBn(TOOL_COUNT)}+` : `${TOOL_COUNT}+` }, hideClass: 'nav:hidden' },
    { path: '/jobs',                  labelKey: 'navigation.jobsMenu',            Icon: IconBriefcase, iconImg: '/module-icons/job.svg',
      // Multi-shop owners: Jobs is stripped from the header entirely, so it must
      // always show here in the sidebar (drop the ≥1550px mirror-hide).
      hideClass: hasMultipleShops ? undefined : 'min-[1550px]:hidden' },
    { path: '/dorkari-links',         labelKey: 'navigation.sorkariLinks',        Icon: IconBuildingBank },
  ].filter((it) => isBdUser || !BD_ONLY_PATHS.includes(it.path));

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    // /tools is the tools hub only — don't treat /tools/form-auto-fillup as SohozTools.
    if (path === '/tools') {
      return location.pathname === '/tools' || location.pathname === '/tools/';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const renderNavItem = (item) => {
    const active = !item.external && isActive(item.path);
    const label = t(item.labelKey) || item.labelKey.split('.').pop();

    const className = cn(
      'flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] transition-colors',
      active
        ? 'bg-primary/10 dark:bg-primary/20 text-[#1C1B19] dark:text-zinc-100 font-semibold'
        : 'text-[#2A2723] dark:text-zinc-200 font-medium hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70',
      // Mirror of the header breakpoint — hides this row once the same menu
      // appears in the main header (see the toolItems/primaryItems comment).
      // …unless the header ran out of room and gave the tab up (then it must show here).
      fitHidden?.has(item.path) ? undefined : item.hideClass
    );
    const inner = (
      <>
        {item.iconImg ? (
          <ModuleGlyph src={item.iconImg} size="md" />
        ) : (
          <item.Icon
            size={18}
            stroke={2.4}
            className={cn('shrink-0', item.iconClass || (active ? 'text-primary' : 'text-[#57534E] dark:text-zinc-400'))}
          />
        )}
        <span className="flex-1 truncate text-[14px]">{label}</span>
        {item.badge && (
          <span
            className={cn(
              'ml-auto text-[9.5px] font-bold px-[7px] py-0.5 rounded-[6px]',
              item.badge.tone === 'orange'
                ? 'text-primary bg-primary/10 dark:text-primary dark:bg-primary/20'
                : 'text-[#3A6FA0] bg-[#EAF0F6] dark:text-sky-300 dark:bg-sky-950/40'
            )}
          >
            {item.badge.text}
          </span>
        )}
      </>
    );

    if (item.external) {
      return (
        <a key={item.href} href={item.href} target="_blank" rel="noreferrer" onClick={onClose} className={className}>
          {inner}
        </a>
      );
    }
    return (
      <Link key={item.path} to={item.path} onClick={onClose} className={className}>
        {inner}
      </Link>
    );
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="left"
        className="p-0 flex flex-col h-full w-[308px] max-w-[85vw] z-[99999] font-hind bg-[#FBFAF8] dark:bg-zinc-900 border-r border-[#ECE9E3] dark:border-zinc-800"
        showClose={false}
      >
        <SheetTitle className="sr-only">{t('navigation.allMenu')}</SheetTitle>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#ECE9E3] dark:border-zinc-800">
          <Link to="/" onClick={onClose} className="flex items-center">
            <Logo w={112} />
          </Link>
          <button
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="w-8 h-8 rounded-[9px] border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-800 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
          >
            <IconX size={16} stroke={2} />
          </button>
        </div>

        {/* ── Nav ── */}
        <div className="flex-1 overflow-y-auto px-3 pt-2 pb-2.5">

          {/* Group 0 — account & credit shortcuts (pinned to the top) */}
          <div className="flex flex-col gap-0.5">
            {/* Buy credit (ক্রেডিট কিনুন) — highlighted CTA; non-admin owners only */}
            {user?.role !== 'admin' && !isOperator && (
              <Link
                to={buyCreditPath}
                onClick={onClose}
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-[10px] text-[14px] font-bold text-white bg-gradient-to-r from-primary to-amber-400 hover:opacity-95 shadow-sm transition-opacity"
              >
                <IconShoppingCart size={18} stroke={2.4} className="text-white shrink-0" />
                <span className="truncate text-[14px]">{t('payments.buyTitle') || 'ক্রেডিট কিনুন'}</span>
              </Link>
            )}
            {/* Credit usage — shows the current credit as a badge */}
            {showCredit && (
              <Link
                to="/settings?tab=credits"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
              >
                <IconCoins size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                <span className="flex-1 truncate text-[14px]">{t('navigation.creditUsage') || 'ক্রেডিট ব্যবহার'}</span>
                <span className={cn('ml-auto text-[13px] font-bold px-2 py-0.5 rounded', (user?.credit ?? 0) < 10 ? 'bg-red-600 text-white' : 'bg-blue-600 text-white')}>
                  {fmtCredit(user?.credit)}
                </span>
              </Link>
            )}
            <Link
              to="/profile"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconUser size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 truncate text-[14px]">{t('navigation.profile') || 'প্রোফাইল'}</span>
            </Link>
          </div>

          <div className="h-px bg-[#F1EFEA] dark:bg-zinc-800 mx-3 my-1.5" />

          {/* Group 1 — no label (blank) */}
          <div className="flex flex-col gap-0.5">
            {primaryItems.map(renderNavItem)}
          </div>

          <div className="h-px bg-[#F1EFEA] dark:bg-zinc-800 mx-3 my-1.5" />

          {/* Section: ফিচার ও টুলস (Features & Tools) */}
          <p className="text-[14px] font-bold text-[#A8A294] dark:text-zinc-500 uppercase tracking-[0.05em] px-3 pt-0.5 pb-1">
            {t('navigation.sectionFeaturesTools')}
          </p>
          <div className="flex flex-col gap-0.5">
            {toolItems.map(renderNavItem)}
          </div>

          {/* Section: হিসাব ও ইনভয়েস — income/expense ledger, invoice & customers */}
          {user?.role !== 'admin' && (
            <>
              <div className="h-px bg-[#F1EFEA] dark:bg-zinc-800 mx-3 my-1.5" />
              <p className="text-[14px] font-bold text-[#A8A294] dark:text-zinc-500 uppercase tracking-[0.05em] px-3 pt-0.5 pb-1">
                {t('navigation.sectionFinance') || 'হিসাব ও ইনভয়েস'}
              </p>
              <div className="flex flex-col gap-0.5">
                <Link
                  to="/income"
                  onClick={onClose}
                  className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
                >
                  <IconCurrencyTaka size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                  <span className="flex-1 truncate text-[14px]">{t('navigation.incomeExpense') || 'আয় ব্যয় হিসেব'}</span>
                </Link>
                <Link
                  to="/income?tab=denapawna"
                  onClick={onClose}
                  className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
                >
                  <IconArrowsExchange size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                  <span className="flex-1 truncate text-[14px]">{t('incomeExpense.denaPawnaTab') || 'দেনা পাওনা'}</span>
                </Link>
                <Link
                  to="/income?tab=invoice"
                  onClick={onClose}
                  className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
                >
                  <IconFileInvoice size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                  <span className="flex-1 truncate text-[14px]">{t('incomeExpense.invoiceTab') || 'ইনভয়েস'}</span>
                </Link>
                <Link
                  to="/income?tab=customers"
                  onClick={onClose}
                  className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
                >
                  <IconUsers size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                  <span className="flex-1 truncate text-[14px]">{t('incomeExpense.customersTab') || 'গ্রাহক'}</span>
                </Link>
                {showIEBtn && (
                  <button
                    onClick={() => { onClose(); onOpenIncomeExpense(); }}
                    className="w-full flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
                  >
                    <IconPlus size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                    <span className="flex-1 text-left truncate text-[14px]">{t('incomeExpense.quickAdd') || 'আয় / ব্যয় যোগ করুন'}</span>
                  </button>
                )}
                <Link
                  to="/settings?tab=incomeexpense"
                  onClick={onClose}
                  className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
                >
                  <IconReceipt2 size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                  <span className="flex-1 truncate text-[14px]">{t('navigation.incomeExpenseList') || 'আয় ব্যয় তালিকা'}</span>
                </Link>
              </div>
            </>
          )}

          <div className="h-px bg-[#F1EFEA] dark:bg-zinc-800 mx-3 my-1.5" />

          {/* Section: অন্যান্য (preserves account / utility actions) */}
          <p className="text-[14px] font-bold text-[#A8A294] dark:text-zinc-500 uppercase tracking-[0.05em] px-3 pt-0.5 pb-1">
            {t('navigation.sectionOther')}
          </p>
          <div className="flex flex-col gap-0.5">
            {user?.role !== 'admin' && (
              <Link
                to="/settings?tab=photoeditsettings"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
              >
                <IconSparkles size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                <span className="flex-1 truncate text-[14px]">{t('profile.defaultPrompts') || 'ফটো এডিট সেটিংস'}</span>
              </Link>
            )}
            <Link
              to="/settings"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconSettings size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 truncate text-[14px]">{t('settings.title') || 'সেটিংস'}</span>
            </Link>
            <Link
              to="/officespaces"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconBuilding size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 truncate text-[14px]">{t('navigation.officeSpaces') || 'দোকান ম্যানেজ করুন'}</span>
            </Link>
            <a
              href={`https://wa.me/8801700559595?text=${encodeURIComponent('আসসালামু আলাইকুম, আমি সহজ কাজ সম্পর্কে জানতে চাই।')}`}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconBrandWhatsapp size={18} stroke={2.4} className="text-[#25D366] shrink-0" />
              <span className="flex-1 truncate text-[14px]">{t('navigation.whatsappMessage')}</span>
            </a>
            <a
              href="https://chat.whatsapp.com/CWnBikjhgO767CR1AhtjuM?s=cl&p=a&mlu=4"
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconUsersGroup size={18} stroke={2.4} className="text-[#25D366] shrink-0" />
              <span className="flex-1 truncate text-[14px]">WhatsApp {t('support.whatsappFloat.supportGroup') || 'সাপোর্ট গ্রুপ'}</span>
            </a>
            {/* Language chooser — label + EN/বাং toggle */}
            <div className="flex items-center gap-3 px-3 py-[5px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200">
              <IconLanguage size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 truncate text-[14px]">{t('navigation.language') || 'ভাষা'}</span>
              <LanguageSwitcher />
            </div>
            {/* Dark mode + keyboard shortcuts: moved up from the fixed footer so the bottom of the drawer stays small */}
            <ThemeRow />
            <button
              type="button"
              onClick={() => setShortcutsOpen(true)}
              className="w-full flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconKeyboard size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 text-left truncate text-[14px]">{t('shortcuts.keyboardShortcuts')}</span>
            </button>
            {(user?.role === 'OWNER' || user?.role === 'OPERATOR') && user?.settings?.videoPlayed !== true && (
              <button
                onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('sohozkaj:open-video')); }}
                className="w-full flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
              >
                <IconPlayerPlay size={18} stroke={2.4} className="text-red-500 shrink-0" />
                <span className="flex-1 text-left truncate text-[14px]">{t('support.introduceVideo')}</span>
              </button>
            )}
          </div>

          <div className="mt-3">
            <ProfileCompletionBanner mobile authReady={authReady} />
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-[#ECE9E3] dark:border-zinc-800 px-4 py-2.5">
          {/* The fixed bottom stays small: profile + app link. Dark mode, language and keyboard shortcuts are rows in the menu above;
              the office-space switcher stays in the top bar for multi-shop users ("Manage shops" in the menu opens the full list). */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleProfileClick}
              className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
            >
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-amber-400 flex items-center justify-center text-white font-semibold text-[13px] shrink-0 overflow-hidden">
                {user?.profilePicture ? (
                  <img src={user.profilePicture} alt={user?.name || 'User'} className="w-full h-full object-cover" />
                ) : (
                  getInitials(user?.name)
                )}
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-[15px] font-semibold text-[#1C1B19] dark:text-zinc-100 truncate">
                  {user?.name || user?.email || 'User'}
                </span>
                <span className="block text-[13px] text-[#A8A294] dark:text-zinc-500 font-mono truncate">
                  {user?.phone || user?.email}
                </span>
              </span>
            </button>
            <button
              onClick={handleLogout}
              aria-label={t('navigation.logout')}
              title={t('navigation.logout')}
              className="w-9 h-9 rounded-[9px] border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-800 flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors shrink-0"
            >
              <IconLogout size={16} />
            </button>
          </div>
          <a
            href={MOBILE_APP_URL}
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
            className="mt-2 flex items-center gap-2.5 px-3 py-2 rounded-[11px] border border-black bg-black hover:bg-zinc-900 transition-colors"
          >
            <img src="/images/google-play.svg" alt="" className="w-5 h-5 shrink-0" />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-[13px] font-semibold text-white truncate">
                {t('navigation.mobileApp') || 'সহজকাজ মোবাইল অ্যাপ'}
              </span>
              <span className="block text-[11px] text-zinc-400 truncate">
                {t('navigation.downloadApp') || 'ডাউনলোড করুন'}
              </span>
            </span>
            <IconDownload size={15} stroke={2.2} className="text-zinc-400 shrink-0" />
          </a>
        </div>
        <UserShortcutHelpModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} showTrigger={false} />
      </SheetContent>
    </Sheet>
  );
}

// Dark-mode row for the drawer menu: label + switch. Uses the same light/dark state as the toggle button (useColorScheme).
function ThemeRow() {
  const { t } = useTranslation();
  const [isDark, toggle] = useColorScheme();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      onClick={toggle}
      className="w-full flex items-center gap-3 px-3 py-[7px] rounded-[10px] text-[14px] font-medium text-[#2A2723] dark:text-zinc-200 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
    >
      {isDark
        ? <IconSun size={18} stroke={2.4} className="text-yellow-500 shrink-0" />
        : <IconMoon size={18} stroke={2.4} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />}
      <span className="flex-1 text-left truncate text-[14px]">{t('navigation.darkMode')}</span>
      <span aria-hidden="true" className={cn('relative h-[18px] w-8 shrink-0 rounded-full transition-colors', isDark ? 'bg-primary' : 'bg-zinc-300 dark:bg-zinc-600')}>
        <span className={cn('absolute left-[2px] top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow transition-transform', isDark && 'translate-x-[14px]')} />
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Create bottom sheet
// ─────────────────────────────────────────────────────────────
function MobileCreateSheet({ open, onClose }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isBdUser = !user?.country || user.country === 'BD';

  const options = [
    {
      title: 'ম্যানুয়াল ডকুমেন্ট',
      sub:   'টেমপ্লেট থেকে ডকুমেন্ট বানান',
      iconBg: 'linear-gradient(145deg,#5b9cf6,#1d4ed8)',
      path:  '/template-sets',
      iconImg: '/module-icons/document-set.svg',
    },
    {
      title: 'এআই ছবি এডিট',
      sub:   'এআই দিয়ে ছবি এডিট করুন',
      iconBg: 'linear-gradient(145deg,#34d399,#059669)',
      path:  '/generate-image',
      iconImg: '/module-icons/ai-tools.svg',
    },
    {
      title: 'ম্যানুয়াল ছবি এডিট',
      sub:   'নিজে ছবি ডিজাইন করুন',
      iconBg: 'linear-gradient(145deg,#22d3ee,#0891b2)',
      path:  '/generate-image-manual',
      iconImg: '/module-icons/manual-photo-edit.svg',
    },
    {
      title: t('navigation.studioEditor'),
      sub:   t('studioEditor.createSub'),
      iconBg: 'linear-gradient(145deg,#22E5C5,#14b8a6)',
      path:  '/studio-editor',
      iconImg: '/module-icons/studio-editor.svg',
    },
    {
      title: 'বাল্ক ছবি এডিট',
      sub:   'একসাথে অনেক ছবি এডিট করুন',
      iconBg: 'linear-gradient(145deg,#f472b6,#db2777)',
      path:  '/bulk-photo-edit',
      iconImg: '/module-icons/bulk-photo-editing.svg',
    },
    {
      title: 'এআই ডকুমেন্ট',
      sub:   'এআই দিয়ে ডকুমেন্ট বানান',
      iconBg: 'linear-gradient(145deg,#E68A00,#FBA53A)',
      path:  '/ai-documents',
      iconImg: '/module-icons/ai-documents.svg',
    },
    {
      title: 'এআই টেমপ্লেট',
      sub:   'এআই দিয়ে নতুন কন্টেন্ট বানান',
      iconBg: 'linear-gradient(145deg,#a78bfa,#6d28d9)',
      path:  '/ai-templates',
      iconImg: '/module-icons/ai-templates.svg',
    },
    {
      title: 'প্রিন্ট মিডিয়া',
      sub:   'ভিজিটিং কার্ড ও ব্যানার',
      iconBg: 'linear-gradient(145deg,#fb923c,#ea580c)',
      path:  '/print-media',
      iconImg: '/module-icons/print-media.svg',
    },
  ].filter((it) => isBdUser || it.path !== '/template-sets');

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="bottom"
        className="nav:hidden p-0 rounded-t-[28px] z-[99999] bg-zinc-100 dark:bg-zinc-900"
        showClose={false}
      >
        <SheetTitle className="sr-only">Create New</SheetTitle>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
          <div>
            <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100 leading-tight">নতুন তৈরি করুন</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">আপনি কী তৈরি করতে চান?</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-300 flex-shrink-0"
          >
            <IconX size={15} />
          </button>
        </div>

        {/* Options */}
        <div className="px-3 pb-6 flex flex-col gap-1.5">
          {options.map((opt) => (
            <button
              key={opt.path}
              onClick={() => { navigate(opt.path); onClose(); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-white dark:bg-zinc-800 shadow-[0_1px_3px_rgba(0,0,0,0.07)] text-left active:scale-[0.98] transition-transform"
            >
              <div
                className="w-11 h-11 rounded-[16px] flex items-center justify-center flex-shrink-0 shadow-[0_3px_8px_rgba(0,0,0,0.18)]"
                style={{ background: opt.iconBg }}
              >
                <img
                  src={tileGlyphSrc(opt.iconImg)}
                  alt=""
                  draggable="false"
                  className={cn('object-contain', isSelfColored(opt.iconImg) ? 'w-[30px] h-[30px]' : 'w-[22px] h-[22px] brightness-0 invert')}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-zinc-900 dark:text-zinc-100 leading-tight">{opt.title}</p>
                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">{opt.sub}</p>
              </div>
              <div className="w-6 h-6 rounded-full bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center flex-shrink-0">
                <IconChevronRight size={12} className="text-zinc-400 dark:text-zinc-300" />
              </div>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default PrivateLayout;
